import { ArrowRight, BarChart3, BookMarked, Brain, Calculator, Calendar, FileDown, LineChart, Shield, TrendingUp, Upload, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';

// ─── Feature grid ─────────────────────────────────────────────────────────

const FEATURES = [
  { icon: LineChart,  title: 'Trade Tracking',        desc: 'Log every trade with entries, exits, fees, and P&L. Manual entry or automatic broker sync.',   color: 'emerald' },
  { icon: BookMarked, title: 'Trading Journal',        desc: 'Attach notes, screenshots, and market context to each trade. Build a searchable decision record.', color: 'emerald' },
  { icon: BarChart3,  title: 'Performance Analytics',  desc: 'Win rate, average R, profit factor, time-of-day heatmaps, streak analysis, and edge scoring.', color: 'emerald' },
  { icon: Calendar,   title: 'Trading Calendar',       desc: 'Monthly P&L calendar. Spot your best days, weeks, and market conditions at a glance.',           color: 'emerald' },
  { icon: Brain,      title: 'AI Edge Analysis',       desc: 'Statistical edge computed from your real trades — surfaces which symbols, hours, and setups consistently win for you.', color: 'emerald' },
  { icon: Zap,        title: 'Broker Sync',            desc: 'OAuth to Alpaca and Charles Schwab. Trades sync automatically — no manual CSV wrangling.',      color: 'emerald' },
  { icon: Shield,     title: 'Playbooks',              desc: 'Build your personal trading playbooks. Define setups, rules, and conditions before you trade.',   color: 'emerald' },
  { icon: TrendingUp, title: 'Position Dashboard',     desc: 'Open positions, cost basis, unrealized P&L, and options analysis in one view.',                  color: 'emerald' },
  { icon: Calculator, title: 'Position Sizer',         desc: 'Built-in position sizing calculator — enter risk tolerance and stop-loss, get exact share count.', color: 'emerald' },
  { icon: Upload,     title: 'CSV Import',             desc: 'Import trades from any broker via CSV. Automatic field mapping for common export formats.',       color: 'emerald' },
];

// ─── Key differentiators (left column) ────────────────────────────────────

const DIFFERENTIATORS = [
  { emoji: '🔗', text: 'Direct broker OAuth — trades sync automatically from Alpaca and Charles Schwab' },
  { emoji: '🧠', text: 'AI edge analysis from your actual trade history — win rate by symbol, hour, session, and setup' },
  { emoji: '📓', text: 'Journal entries tied to trades — build your decision log trade by trade' },
  { emoji: '🔒', text: 'Your data stays yours — run AI locally via Ollama, no data sold to third parties' },
];

// ─── Fake dashboard preview (right column) ────────────────────────────────

function DashboardPreview() {
  const bars = [55, 80, 45, 90, 68, 75, 40, 88, 62, 95, 50, 72];
  return (
    <div className="relative">
      {/* Glow */}
      <div className="absolute inset-0 scale-90 translate-y-4 bg-emerald-500/15 rounded-2xl blur-3xl pointer-events-none" />

      <div className="relative bg-slate-900 border border-slate-700/60 rounded-2xl overflow-hidden shadow-2xl shadow-black/60">
        {/* Title bar */}
        <div className="flex items-center gap-2 px-4 py-3 bg-slate-800/80 border-b border-slate-700/60">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500/70" />
            <div className="w-3 h-3 rounded-full bg-amber-500/70" />
            <div className="w-3 h-3 rounded-full bg-emerald-500/70" />
          </div>
          <span className="text-slate-400 text-xs ml-2">Trading Journal — Dashboard</span>
        </div>

        <div className="p-5">
          {/* Metric row */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            {[
              { label: 'Win Rate',   value: '68.3%',   up: true  },
              { label: 'Total P&L',  value: '+$12,450', up: true  },
              { label: 'Avg R:R',    value: '2.3×',    up: true  },
            ].map(({ label, value, up }) => (
              <div key={label} className="bg-slate-800/60 rounded-xl p-3 text-center">
                <div className={`text-lg font-bold ${up ? 'text-emerald-400' : 'text-red-400'}`}>{value}</div>
                <div className="text-slate-500 text-xs mt-0.5">{label}</div>
              </div>
            ))}
          </div>

          {/* Mini bar chart */}
          <div className="bg-slate-800/40 rounded-xl p-3 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-400 text-xs font-medium">Monthly P&L</span>
              <span className="text-emerald-400 text-xs">+$3,210 this month</span>
            </div>
            <div className="flex items-end gap-1 h-14">
              {bars.map((h, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-sm"
                  style={{
                    height: `${h}%`,
                    background: h > 60
                      ? 'linear-gradient(to top, #10b981, #34d399)'
                      : 'linear-gradient(to top, #374151, #4b5563)',
                    opacity: 0.85,
                  }}
                />
              ))}
            </div>
          </div>

          {/* Recent trades */}
          <div className="space-y-2">
            <div className="text-slate-400 text-xs font-medium mb-2">Recent Trades</div>
            {[
              { symbol: 'AAPL',  side: 'LONG',  pnl: '+$340', date: 'Apr 24' },
              { symbol: 'SPY',   side: 'SHORT', pnl: '+$210', date: 'Apr 23' },
              { symbol: 'NVDA',  side: 'LONG',  pnl: '-$85',  date: 'Apr 22' },
            ].map(({ symbol, side, pnl, date }) => {
              const isWin = pnl.startsWith('+');
              return (
                <div key={symbol} className="flex items-center justify-between bg-slate-800/40 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-white text-xs font-semibold w-10">{symbol}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${side === 'LONG' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>{side}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-slate-500 text-xs">{date}</span>
                    <span className={`text-xs font-bold ${isWin ? 'text-emerald-400' : 'text-red-400'}`}>{pnl}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Streak badge */}
          <div className="mt-4 flex items-center justify-between bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-4 py-2.5">
            <span className="text-emerald-300 text-xs font-medium">🔥 Current Win Streak</span>
            <span className="text-emerald-400 text-lg font-bold">5</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────

export default function FeaturesSection({ isAuthenticated }) {
  return (
    <div className="bg-[#0d1e35] py-24 px-6 border-y border-slate-800/60">
      <div className="max-w-7xl mx-auto">

        {/* Section header — same scale as BookSection */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-4 py-1.5 mb-4">
            <TrendingUp size={13} className="text-emerald-400" />
            <span className="text-emerald-300 text-xs font-semibold tracking-wide uppercase">The Trading Journal</span>
          </div>
          <h2 className="text-4xl font-bold text-white mb-4">
            Professional Trading Journal
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-lg">
            The tool that turns the book's strategies into{' '}
            <span className="text-emerald-400">measurable, accountable results</span> —
            tracking every trade, every decision, and every edge you build.
          </p>
        </div>

        {/* Two-column layout — mirrors BookSection */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">

          {/* Left: description + differentiators + stats */}
          <div>
            <div className="bg-slate-800/30 border border-emerald-500/20 rounded-2xl p-8 mb-8">
              <blockquote className="text-xl text-slate-200 italic leading-relaxed mb-4">
                "Knowledge without execution is entertainment. The Journal is where the book becomes real."
              </blockquote>
              <p className="text-emerald-400 font-semibold text-sm">— Built for traders who take it seriously</p>
            </div>

            <p className="text-slate-300 leading-relaxed mb-6">
              Most traders lose not because they lack knowledge — they lose because they never measure their own behavior. The <span className="text-white font-medium">Trading Journal</span> gives you an honest, data-driven mirror of every decision you make.
            </p>

            <div className="space-y-3 mb-8">
              {DIFFERENTIATORS.map(({ emoji, text }) => (
                <div key={text} className="flex items-start gap-3 bg-slate-800/20 border border-slate-700/30 rounded-xl px-4 py-3">
                  <span className="text-lg flex-shrink-0">{emoji}</span>
                  <p className="text-slate-300 text-sm leading-relaxed">{text}</p>
                </div>
              ))}
            </div>

            {/* Stats — mirrors the book's 506 / 50+ / 7 row */}
            <div className="grid grid-cols-3 gap-4">
              {[
                ['Free', 'to start'],
                ['2', 'live broker integrations'],
                ['AI', 'edge analysis'],
              ].map(([num, label]) => (
                <div key={label} className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-emerald-400">{num}</div>
                  <div className="text-slate-400 text-xs mt-1">{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: dashboard preview — mirrors book cover */}
          <DashboardPreview />
        </div>

        {/* Feature grid — 3-col, more breathing room than old 4-col */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-14">
          {FEATURES.map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              className="group bg-emerald-500/5 hover:bg-emerald-500/10 border border-emerald-500/15 hover:border-emerald-400/40 rounded-2xl p-5 transition-all hover:scale-[1.02]"
            >
              <div className="w-10 h-10 bg-emerald-500/15 rounded-xl flex items-center justify-center mb-4">
                <Icon size={20} className="text-emerald-400" />
              </div>
              <h3 className="text-white font-semibold mb-2 text-sm">{title}</h3>
              <p className="text-slate-400 text-xs leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>

        {/* Connection callout */}
        <div className="bg-gradient-to-r from-emerald-500/8 via-slate-800/30 to-amber-500/8 border border-slate-700/40 rounded-2xl p-8 flex flex-col md:flex-row items-center gap-8">
          <div className="flex-shrink-0 text-4xl">📘 + 📈</div>
          <div className="flex-1">
            <h3 className="text-white font-bold text-xl mb-2">The book + the journal = the complete system</h3>
            <p className="text-slate-400 leading-relaxed">
              <span className="text-amber-300 font-medium">Wealth At Any Age</span> Chapter 30 covers trading and investing edge specifically. Use the Journal to implement those strategies, measure your edge, and build the discipline that converts market knowledge into consistent returns.
            </p>
          </div>
          <div className="flex-shrink-0">
            {isAuthenticated ? (
              <Link
                to="/"
                className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-6 py-3 rounded-xl transition-colors whitespace-nowrap"
              >
                Open Dashboard <ArrowRight size={16} />
              </Link>
            ) : (
              <Link
                to="/login?mode=register"
                className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-6 py-3 rounded-xl transition-colors whitespace-nowrap"
              >
                Start Free <ArrowRight size={16} />
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
